import React, { useMemo, useState } from 'react';
import type { Message } from "@interfaces/chat.interface";
import './CallMessage.css';

const AUTH_CONFIG_KEY = "kido_auth_config";

interface CallMessageProps {
  message: Message;
  duration?: number;
  status?: 'normal' | 'missed' | 'answered';
  onCallBack?: (userId: string, userName: string) => void;
  isChatDrawer?: boolean;
}

const formatDuration = (durationInMilliseconds?: number): string => {
  if (!durationInMilliseconds || durationInMilliseconds === 0) {
    return "0 phút 0 giây";
  }
  const durationInSeconds = Math.floor(durationInMilliseconds / 1000);
  if (durationInSeconds < 60) {
    return `0 phút ${durationInSeconds} giây`;
  }
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds % 60;
  return `${minutes} phút ${seconds} giây`;
};

const getCallStatus = (message: Message, duration?: number): string => {
  if (!duration || duration === 0) {
    return "Người nhận từ chối";
  }
  const direction = message.meta?.direction as string | undefined;
  if (direction === 'OUTBOUND' || direction === 'OUTGOING') {
    return "Cuộc gọi thoại đi";
  } else {
    return "Cuộc gọi thoại đến";
  }
};

export const CallMessage: React.FC<CallMessageProps> = React.memo(({ 
  message, 
  duration,
  onCallBack,
  isChatDrawer = false,
}) => {
  const [isCallEnabled] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(AUTH_CONFIG_KEY);
      if (!raw) return true;
      const cfg = JSON.parse(raw);
      return cfg.enableCall !== false;
    } catch {
      return true;
    }
  });

  const formattedDurationText = useMemo(() => formatDuration(duration), [duration]);
  const callStatus = useMemo(() => getCallStatus(message, duration), [message, duration]);

  const handleCallBack = () => {
    if (onCallBack) {
      const userId = message.sender.id;
      const userName = message.sender.name || "Người dùng";
      onCallBack(userId, userName);
    }
  };

  const receiverId = message.meta?.receiverId as string | undefined;
  const senderId = message.sender.id;

  return (
    <div className="call-message">
      <div className="call-message__header">
        {callStatus}
      </div>
      <div className="call-message__main">
        <div className="call-message__icon">
          📞
        </div>
        <div className="call-message__content">
          <div className="call-message__title">
            Cuộc gọi thoại
          </div>
          <div className="call-message__duration">
            {formattedDurationText}
          </div>
        </div>
      </div>
      {!isChatDrawer && isCallEnabled && (
        <div className="call-message__actions">
          <button
            className={`call-back-button ${!receiverId && !senderId ? 'disabled' : ''}`}
            onClick={handleCallBack}
            disabled={!receiverId && !senderId}
            title={receiverId || senderId ? `Gọi lại` : 'Không thể gọi lại'}
          >
            GỌI LẠI
          </button>
        </div>
      )}
    </div>
  );
});

CallMessage.displayName = 'CallMessage';

export default CallMessage;

